import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { GameService } from '../../core/state/game.service';
import { CompetitionService } from '../../core/http/competition.service';
import { NewsItem } from '../../core/models/types';
import { IconComponent } from '../../shared/ui/icon/icon.component';
import { PlayerProfileComponent } from '../../shared/ui/player-profile/player-profile.component';
import { newsPlayerTitle, newsProfileTargets } from './news-player-title';
import { NewsReadState } from '../../core/state/news-read.state';
import { newsAwardTitle } from './news-award-title';
import { newsSponsorTitle } from './news-sponsor-title';

export interface NewsDayGroup {
  dateKey: string;
  displayDate: string;
  isToday: boolean;
  isYesterday: boolean;
  items: NewsItem[];
}

@Component({
  selector: 'app-news',
  standalone: true,
  imports: [CommonModule, IconComponent, PlayerProfileComponent],
  templateUrl: './news.component.html',
  styles: [':host { display: block; }'],
})
export class NewsComponent implements OnInit {
  readonly game = inject(GameService);
  private readonly newsRead = inject(NewsReadState);
  private readonly competitionService = inject(CompetitionService);
  private readonly router = inject(Router);
  readonly liveNews = signal<NewsItem[]>([]);
  readonly isLoadingNews = signal(true);
  readonly newsError = signal<string | null>(null);

  readonly selectedNewsPlayer = signal<{ id: string; name: string } | null>(null);
  readonly newsPlayerTitle = newsPlayerTitle;
  readonly newsAwardTitle = newsAwardTitle;
  readonly newsSponsorTitle = newsSponsorTitle;

  openAwardPlayer(playerDefinitionId: string, name: string): void {
    this.selectedNewsPlayer.set({ id: playerDefinitionId, name });
  }

  openNewsPlayer(item: NewsItem): void {
    const target = newsProfileTargets(item);
    if (newsPlayerTitle(item) && target.playerId) {
      this.selectedNewsPlayer.set({ id: target.playerId, name: target.playerName || 'Jugador' });
    }
  }

  openNewsClub(item: NewsItem, clubName: string): void {
    const facts = item.facts as any;
    const clubId = newsProfileTargets(item).clubId || facts?.clubId || facts?.sellerClubId;
    if (clubId) {
      this.game.openClubProfile(clubId, clubName);
      return;
    }
    const standingsMatch = this.game.standings().find(r => r.clubName?.toLowerCase() === clubName.toLowerCase());
    if (standingsMatch) {
      this.game.openClubProfile(standingsMatch.clubId, clubName, standingsMatch.clubCode);
      return;
    }
    const squadClubs = this.game.clubs ? this.game.clubs() : [];
    const clubMatch = squadClubs.find(c => c.name?.toLowerCase() === clubName.toLowerCase());
    if (clubMatch) {
      this.game.openClubProfile(clubMatch.id, clubName, clubMatch.code || clubMatch.shortName);
    }
  }
  // Noticias agrupadas cronológicamente por día (formato simple)
  readonly groupedNews = computed<NewsDayGroup[]>(() => {
    const list = this.liveNews();
    if (!list || list.length === 0) return [];

    const pad = (n: number) => String(n).padStart(2, '0');
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = `${yesterday.getFullYear()}-${pad(yesterday.getMonth() + 1)}-${pad(yesterday.getDate())}`;

    const map = new Map<string, NewsItem[]>();

    for (const item of list) {
      // Priorizar siempre publishedAt (fecha oficial de publicación de la noticia) sobre occurredAt
      const rawDate = item.publishedAt || item.occurredAt;
      let dateKey = 'Sin fecha';
      if (rawDate) {
        try {
          const d = new Date(rawDate);
          if (!isNaN(d.getTime())) {
            dateKey = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
          } else {
            dateKey = rawDate.slice(0, 10);
          }
        } catch {
          dateKey = rawDate.slice(0, 10);
        }
      }

      if (!map.has(dateKey)) {
        map.set(dateKey, []);
      }
      map.get(dateKey)!.push(item);
    }

    // Ordenar los items dentro de cada grupo por fecha más reciente
    for (const [, items] of map) {
      items.sort((a, b) => {
        const timeA = new Date(a.publishedAt || a.occurredAt || 0).getTime();
        const timeB = new Date(b.publishedAt || b.occurredAt || 0).getTime();
        return timeB - timeA;
      });
    }

    // Ordenar fechas en orden descendente (el día más reciente primero)
    const sortedKeys = Array.from(map.keys()).sort((a, b) => b.localeCompare(a));

    return sortedKeys.map((dateKey) => {
      let displayDate = dateKey;
      let isToday = false;
      let isYesterday = false;

      if (dateKey !== 'Sin fecha') {
        const parts = dateKey.split('-');
        if (parts.length === 3) {
          const year = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const day = parseInt(parts[2], 10);
          const d = new Date(year, month, day);

          isToday = dateKey === todayStr;
          isYesterday = dateKey === yesterdayStr;

          const formattedDate = d.toLocaleDateString('es-ES', {
            day: 'numeric',
            month: 'long',
          });

          if (isToday) {
            displayDate = `Hoy · ${formattedDate}`;
          } else if (isYesterday) {
            displayDate = `Ayer · ${formattedDate}`;
          } else {
            displayDate = d.toLocaleDateString('es-ES', {
              day: 'numeric',
              month: 'long',
              year: year !== now.getFullYear() ? 'numeric' : undefined,
            });
          }
        }
      }

      return {
        dateKey,
        displayDate,
        isToday,
        isYesterday,
        items: map.get(dateKey)!,
      };
    });
  });

  async ngOnInit(): Promise<void> {
    await this.loadNews();
  }

  async loadNews(forceRefresh = true): Promise<void> {
    this.isLoadingNews.set(true);
    this.newsError.set(null);
    const userId = this.game.currentUser()?.id;
    try {
      const context = await this.competitionService.resolveCurrentCompetitionContext();
      if (context.status !== 'JOINED' || !context.clubId) {
        this.router.navigate(['/leagues']);
        return;
      }
      const state = await this.competitionService.getResolvedCompetitionState();
      if (state) this.game.syncWithCompetitions(state);
      if (!context.clubId) {
        this.liveNews.set([]);
        return;
      }
      const response = await this.game.getNewsByUnit(context.clubId, undefined, forceRefresh, cached => {
        if (userId !== this.game.currentUser()?.id) return;
        this.liveNews.set(cached.items || []);
      });
      if (userId !== this.game.currentUser()?.id) return;
      this.liveNews.set(response?.items || []);
      if (userId && userId === this.game.currentUser()?.id) {
        this.newsRead.markRead(userId, context.clubId, response?.items || []);
      }
    } catch (err: any) {
      this.newsError.set(err?.message || 'Error al recuperar las noticias de la liga.');
    } finally {
      this.isLoadingNews.set(false);
    }
  }

  formatNewsTime(occurredAt?: string, publishedAt?: string): string {
    const dateStr = publishedAt || occurredAt;
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const hours = String(date.getHours()).padStart(2, '0');
      const mins = String(date.getMinutes()).padStart(2, '0');
      const timeStr = `${hours}:${mins}`;

      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMin = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMin / 60);

      if (diffMin >= 0 && diffMin < 1) return `${timeStr} · Ahora`;
      if (diffMin >= 1 && diffMin < 60) return `${timeStr} · Hace ${diffMin}m`;
      if (diffHours >= 1 && diffHours < 12) return `${timeStr} · Hace ${diffHours}h`;

      return timeStr;
    } catch {
      return '';
    }
  }

}
